import {
  getContextFromFeaturePrd,
  getFileContentsWithLineNumbers,
} from "@/components/AI/EngSpec/utils";
import { useSelectedElements } from "@/hooks/useSelectedElements";
import { useAiActionStore } from "@/store/zustand/aiActionStore";
import { useAiContextStore } from "@/store/zustand/aiContextStore";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import useLayoutStore from "@/store/zustand/layoutStore";
import { useFeaturePrdForCanvas } from "@/store/zustand/supabaseStore";
import { useActiveCanvas } from "@/utils/utils";
import { pick } from "lodash";
import { Storyboard } from "../types";
import { AIChatInput } from "./chatWithAI";

export const formatStoryboardForPrompt = (canvasId: string, sb: Storyboard) => {
  return {
    id: sb.id,
    name: sb.name,
    x: sb.x,
    y: sb.y,
    width: sb.width,
    height: sb.height,
    type: sb.type,

    // Make this the relative path (everything after canvas id)
    fullFilePath: sb.fullFilePath?.split(canvasId + "/")[1],
  };
};

export const useBaseInputsForPrompt = () => {
  const featurePrd = useFeaturePrdForCanvas();
  const tokenSoftLimit = useAiActionStore((state) => state.tokenSoftLimit);
  const { layoutMode, leftTab } = useLayoutStore((state) =>
    pick(state, ["layoutMode", "leftTab"])
  );
  const activeCanvas = useActiveCanvas();
  const fileToContents = useCodebaseStore((state) => state.fileToContents);
  const chatContextCodeSnippet = useAiContextStore(
    (state) => state.chatContextCodeSnippet
  );
  const chatContextDiffCodeSnippet = useAiContextStore(
    (state) => state.chatContextDiffCodeSnippet
  );
  const { selectedTreeElements, selectedStoryboards } = useSelectedElements();

  const { userSelectionContext } = getContextFromFeaturePrd(featurePrd);

  const canvasId = activeCanvas?.id;

  // Combine selected elements and storyboards
  const totalUserSelection = [
    ...selectedTreeElements,
    ...selectedStoryboards.map((sb) => ({
      ...sb,
      isStoryboard: true,
    })),
  ];

  // Format storyboards in the canvas for the prompt
  const storyboardsInCanvas = canvasId
    ? activeCanvas?.storyboards
        ?.filter((sb: Storyboard) => !sb.deleted && !sb.preCreated)
        ?.map((sb: Storyboard) => formatStoryboardForPrompt(canvasId, sb))
    : [];

  // Prepare code snippet related fields
  let fileContentsForSnippet: string | undefined = undefined;
  if (chatContextCodeSnippet) {
    const fileContents = fileToContents[chatContextCodeSnippet.filename];
    fileContentsForSnippet = getFileContentsWithLineNumbers(fileContents);
  }

  return {
    feature_title: featurePrd?.title || "",
    feature_prd: featurePrd?.prd || "",
    mermaid_diagram: featurePrd?.mermaid_diagram || "",
    user_selection_context: JSON.stringify(userSelectionContext),
    selected_elements: JSON.stringify(totalUserSelection),
    storyboards_in_canvas: JSON.stringify(storyboardsInCanvas || []),
    token_soft_limit: tokenSoftLimit * 1000,
    layout_mode: layoutMode,
    left_tab: leftTab,
    file_contents: fileContentsForSnippet,
    diff_code_snippet: chatContextDiffCodeSnippet
      ? JSON.stringify(chatContextDiffCodeSnippet)
      : "",
    selected_code_snippet: chatContextCodeSnippet
      ? JSON.stringify(chatContextCodeSnippet)
      : "",
  } satisfies Partial<AIChatInput>;
};
